import React, { useEffect, useState, useCallback } from 'react';
import PageLayout from 'components/PageLayout';
import LoadingState from 'components/LoadingState';
import ErrorState from 'components/ErrorState';
import AccessDenied from 'components/AccessDenied';
import NotAuthenticated from 'components/NotAuthenticated';
import { useAuth } from 'contexts/AuthContext';
import { useRoleInfo } from 'hooks/useRoleInfo';
import {
  fetchCategoryRubricMapping,
  CategoryMapping,
} from 'services/categoryMappingService';
import { CURRENT_AWARD_YEAR } from 'constants/evaluation';
import CategorySection from './CategorySection';
import CSVImporter from './CSVImporter';

// 4 data columns: #, aspect, weight, guidance
const COL_SPAN = 5;

const CategoryMappingPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { userIsSuperAdmin } = useRoleInfo();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mappings, setMappings] = useState<CategoryMapping[]>([]);
  const [expandAllToken, setExpandAllToken] = useState(0);
  const [collapseAllToken, setCollapseAllToken] = useState(0);
  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_AWARD_YEAR);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCategoryRubricMapping(selectedYear);
      setMappings(data);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load category mapping data.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  const handleExportCSV = () => {
    if (mappings.length === 0) return;

    // Header row matching the requested CSV format
    const headers = [
      'id',
      'name',
      'weight',
      'description',
      'scale_min',
      'scale_max',
      'scale_guidance.0',
      'scale_guidance.1',
      'scale_guidance.2',
      'scale_guidance.3',
      'scale_guidance.4',
      'scale_guidance.5',
      'scale_guidance.6',
      'scale_guidance.7',
      'scale_guidance.8',
      'scale_guidance.9',
      'scale_guidance.10',
    ];

    const csvRows = [headers.join(';')];

    mappings.forEach(m => {
      const categoryName = m.categoryName;
      const aspects = m.rubric?.aspects ?? [];

      aspects.forEach(a => {
        const row = [
          a.documentId, // id (Aspect DocumentID)
          `${categoryName} - ${a.name}`, // name: Category - Aspect
          `${a.weight}%`, // weight
          `"${(a.description || '').replace(/"/g, '""')}"`, // description (quoted and escaped)
          a.scaleMin, // scale_min
          a.scaleMax, // scale_max
        ];

        // scale_guidance.0 to 10
        for (let i = 0; i <= 10; i++) {
          const guidance = a.scaleGuidance[String(i)] || '';
          row.push(`"${guidance.replace(/"/g, '""')}"`);
        }

        csvRows.push(row.join(';'));
      });
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `Award_Aspect_Details_${selectedYear}_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (isAuthenticated() && userIsSuperAdmin) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, userIsSuperAdmin, loadData]);

  if (!isAuthenticated()) {
    return (
      <PageLayout title="Category Mapping" subtitle="Authentication required.">
        <NotAuthenticated
          message="Please log in to access the Category Mapping page."
          buttonLink="/"
          buttonClassName="bg-red-smdd text-white hover:bg-red-smdd-dark"
          iconBgClassName="bg-yellow-100"
          iconClassName="text-yellow-600"
          className="text-center py-12"
          buttonText="Go to Home"
        />
      </PageLayout>
    );
  }

  if (!userIsSuperAdmin) {
    return (
      <PageLayout title="Category Mapping" subtitle="Admin access required.">
        <AccessDenied
          message="Access is restricted to Award Administrators only."
          iconBgClassName="bg-red-100"
          iconClassName="text-red-600"
          className="text-center py-12"
          buttonText="Return to Home"
        />
      </PageLayout>
    );
  }

  const totalCategories = mappings.length;
  const totalAspects = mappings.reduce(
    (s, m) => s + (m.rubric?.aspects.length ?? 0),
    0
  );

  let rowCounter = 1;

  return (
    <PageLayout
      title="Category Mapping"
      subtitle="Category → Rubric → Aspect mapping from Strapi CMS."
    >
      {loading ? (
        <LoadingState message="Fetching category mapping data from CMS..." />
      ) : error ? (
        <ErrorState
          title="Error Loading Category Mapping"
          error={error}
          onRetry={loadData}
        />
      ) : (
        <>
          {/* Stats + controls bar */}
          <div className="flex items-center gap-4 mb-3 flex-wrap">
            <span className="text-xs text-gray-500 font-baijamjuree">
              <strong className="text-gray-800">{totalCategories}</strong>{' '}
              categories &middot;{' '}
              <strong className="text-gray-800">{totalAspects}</strong> aspects
              total
            </span>
            <span className="text-xs text-gray-400 font-baijamjuree hidden sm:inline">
              Click <strong>▼ Show</strong> on any row to expand score guidance
              (1–10 scale)
            </span>
            <button
              onClick={() => setExpandAllToken(t => t + 1)}
              className="text-xs font-semibold font-baijamjuree px-3 py-1.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
            >
              + Expand All
            </button>
            <button
              onClick={() => setCollapseAllToken(t => t + 1)}
              className="text-xs font-semibold font-baijamjuree px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              - Collapse All
            </button>
            <div className="ml-auto flex items-center gap-2">
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(Number(e.target.value))}
                className="text-xs font-semibold font-baijamjuree px-2 py-1.5 rounded border border-gray-300 text-gray-700 bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value={2025}>2025 Edition</option>
                <option value={2026}>2026 Edition</option>
              </select>
              <CSVImporter year={selectedYear} onImportComplete={loadData} />
              <button
                onClick={handleExportCSV}
                className="text-xs font-semibold font-baijamjuree px-3 py-1.5 rounded border border-green-200 bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
              >
                ↓ Export CSV
              </button>
              <button
                id="refresh-mapping-btn"
                onClick={loadData}
                className="text-xs font-semibold font-baijamjuree px-3 py-1.5 rounded border border-gray-300 hover:border-gray-500 text-gray-600 hover:text-gray-900 transition-colors"
              >
                ↺ Refresh
              </button>
            </div>
          </div>

          {/* Main table */}
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm bg-white">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b-2 border-gray-300">
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-widest text-gray-500 w-8 text-center">
                    #
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-widest text-gray-500 w-72">
                    Category → Rubric
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-bold uppercase tracking-widest text-gray-500"></th>
                  <th className="px-3 py-2 text-center text-xs font-bold uppercase tracking-widest text-gray-500">
                    Weight
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-widest text-gray-500">
                    Guidance
                  </th>
                </tr>
              </thead>
              <tbody>
                {mappings.map(mapping => {
                  const aspectCount = mapping.rubric?.aspects.length ?? 0;
                  const startRow = rowCounter;
                  rowCounter += aspectCount;
                  return (
                    <CategorySection
                      key={mapping.categoryName}
                      mapping={mapping}
                      globalRowStart={startRow}
                      colSpan={COL_SPAN}
                      expandAllToken={expandAllToken}
                      collapseAllToken={collapseAllToken}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-400 font-baijamjuree">
            <span className="ml-4 font-semibold text-gray-500">
              Score guidance:
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />{' '}
              8–10 High
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />{' '}
              5–7 Mid
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />{' '}
              1–4 Low
            </span>
          </div>
        </>
      )}
    </PageLayout>
  );
};

export default CategoryMappingPage;
